import { refreshAccessToken } from '../../api';
import { AccessToken } from '../access-token';
import { isTokenExpired } from '../access-token/utils';
import TokenExpiredError from '../access-token/TokenExpiredError';
import EventBus from '../event-bus';
import { EVENT_BUS_EVENT } from '../event-bus/enums/event-bus-events';
import { currentSSOService } from '../sso-auth-service/CurrentSSOService';
import { clearAuthData } from '../../actions/auth/utils';

const handleLogout = () => {
  const eventBus = EventBus.getInstance();
  eventBus.dispatch(EVENT_BUS_EVENT.LOGOUT);
  clearAuthData();
};

const EXP_DELTA = 60;

interface StoredTokens {
  accessToken: string | null;
  refreshToken: string | null;
}

export default class RefreshTokenService {
  private static activePromise: Promise<any> | null;

  private static getStoredTokens(): StoredTokens {
    return {
      accessToken: AccessToken.getToken(),
      refreshToken: AccessToken.getRefreshToken(),
    };
  }

  /**
   * Tokens could be refreshed by another browser tab while the refresh request was in flight.
   * In that case local storage holds different (and still present) tokens than the ones
   * captured before the request, so the pending api call can be safely retried.
   */
  private static areTokensRefreshedByAnotherTab(tokensBeforeRefresh: StoredTokens) {
    const { accessToken, refreshToken } = RefreshTokenService.getStoredTokens();
    if (!accessToken || !refreshToken) return false;

    return accessToken !== tokensBeforeRefresh.accessToken
      || refreshToken !== tokensBeforeRefresh.refreshToken;
  }

  public static checkTokens() {
    if (!RefreshTokenService.activePromise) {
      RefreshTokenService.activePromise = new Promise<void>(
        (resolve, reject) => {
          const accessToken = AccessToken.getToken();
          const isAccessExpired = isTokenExpired(accessToken, EXP_DELTA);
          if (!accessToken || !isAccessExpired) {
            if (!accessToken) { AccessToken.clearRefreshToken(); }
            resolve();
          }
          else {
            const tokensBeforeRefresh = RefreshTokenService.getStoredTokens();
            const refreshToken = tokensBeforeRefresh.refreshToken;

            const onError = (e: any) => {
              if (RefreshTokenService.areTokensRefreshedByAnotherTab(tokensBeforeRefresh)) {
                resolve();
                return;
              }
              handleLogout();
              reject(e);
            };

            if (refreshToken) {
              const ssoService = currentSSOService.getSSOService();
              if (ssoService) {
                const apiCall = ssoService.getRefreshTokenApiCall();
                apiCall({ params: undefined }).then(
                  () => resolve(),
                  onError
                );
              } else {
                refreshAccessToken().then(
                  () => resolve(),
                  onError
                );
              }
            } else {
              reject(new TokenExpiredError());
              handleLogout();
            }
          }
        }
      );
      RefreshTokenService.activePromise
        .then(() => (RefreshTokenService.activePromise = null))
        .catch(() => (RefreshTokenService.activePromise = null));
    }
    return RefreshTokenService.activePromise;
  }
}
